/**
 * Tag editor with autocomplete functionality
 */
import { useState, useRef, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';

interface TagEditorProps {
  tags: string[];
  onChange: (tags: string[]) => void;
  availableTags?: string[];
  placeholder?: string;
  maxTags?: number;
  disabled?: boolean;
  className?: string;
}

export function TagEditor({
  tags,
  onChange,
  availableTags = [],
  placeholder = 'Add tag...',
  maxTags = 20,
  disabled = false,
  className = '',
}: TagEditorProps) {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Filter available tags based on input and exclude already selected tags
  const filteredSuggestions = availableTags
    .filter((tag) => !tags.includes(tag) && tag.toLowerCase().includes(inputValue.toLowerCase()))
    .slice(0, 10); // Limit suggestions

  // Handle input change
  const handleInputChange = (value: string) => {
    setInputValue(value);
    setShowSuggestions(value.length > 0 && filteredSuggestions.length > 0);
    setSelectedSuggestionIndex(-1);
  };

  // Add a tag
  const addTag = (tag: string) => {
    const trimmedTag = tag.trim().toLowerCase();

    if (!trimmedTag) return;
    if (tags.includes(trimmedTag)) return;
    if (tags.length >= maxTags) return;

    // Validate tag format (alphanumeric, dash, underscore)
    if (!/^[a-z0-9-_]+$/.test(trimmedTag)) {
      return;
    }

    onChange([...tags, trimmedTag]);
    setInputValue('');
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
  };

  // Remove a tag
  const removeTag = (tagToRemove: string) => {
    onChange(tags.filter((tag) => tag !== tagToRemove));
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions && filteredSuggestions.length > 0) {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedSuggestionIndex((prev) =>
            prev < filteredSuggestions.length - 1 ? prev + 1 : 0
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedSuggestionIndex((prev) =>
            prev > 0 ? prev - 1 : filteredSuggestions.length - 1
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (selectedSuggestionIndex >= 0) {
            addTag(filteredSuggestions[selectedSuggestionIndex]);
          } else if (inputValue.trim()) {
            addTag(inputValue);
          }
          break;
        case 'Escape':
          setShowSuggestions(false);
          setSelectedSuggestionIndex(-1);
          break;
      }
    } else if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      // Remove last tag when backspace is pressed on empty input
      removeTag(tags[tags.length - 1]);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion: string) => {
    addTag(suggestion);
    inputRef.current?.focus();
  };

  // Handle input blur (with delay to allow suggestion clicks)
  const handleBlur = () => {
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }, 150);
  };

  // Handle input focus
  const handleFocus = () => {
    if (inputValue && filteredSuggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Update selected suggestion index when filtered suggestions change
  useEffect(() => {
    if (selectedSuggestionIndex >= filteredSuggestions.length) {
      setSelectedSuggestionIndex(-1);
    }
  }, [filteredSuggestions, selectedSuggestionIndex]);

  // Scroll selected suggestion into view
  useEffect(() => {
    if (selectedSuggestionIndex >= 0 && suggestionsRef.current) {
      const suggestionElements = suggestionsRef.current.querySelectorAll('[data-suggestion]');
      const selectedElement = suggestionElements[selectedSuggestionIndex] as HTMLElement;
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedSuggestionIndex]);

  return (
    <div className={`relative ${className}`}>
      <div className="flex flex-wrap items-center gap-1 p-2 border rounded-md min-h-[36px] focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
        {/* Existing tags */}
        {tags.map((tag) => (
          <Badge key={tag} variant="secondary" className="text-xs">
            {tag}
            {!disabled && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 ml-1 hover:bg-transparent"
                onClick={() => removeTag(tag)}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </Badge>
        ))}

        {/* Input for new tags */}
        {!disabled && tags.length < maxTags && (
          <div className="flex-1 min-w-[120px] relative">
            <Input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleBlur}
              onFocus={handleFocus}
              placeholder={tags.length === 0 ? placeholder : ''}
              className="border-0 p-0 h-6 text-sm shadow-none focus-visible:ring-0"
              disabled={disabled}
            />
          </div>
        )}

        {/* Add button for empty state */}
        {!disabled && tags.length === 0 && !inputValue && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-6 px-2"
            onClick={() => inputRef.current?.focus()}
          >
            <Plus className="h-3 w-3 mr-1" />
            Add tag
          </Button>
        )}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg">
          <ScrollArea className="max-h-[200px]" ref={suggestionsRef}>
            <div className="p-1">
              {filteredSuggestions.map((suggestion, index) => (
                <button
                  key={suggestion}
                  data-suggestion
                  type="button"
                  className={`w-full text-left px-2 py-1.5 text-sm rounded-sm transition-colors ${
                    index === selectedSuggestionIndex
                      ? 'bg-accent text-accent-foreground'
                      : 'hover:bg-accent hover:text-accent-foreground'
                  }`}
                  onClick={() => handleSuggestionClick(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}

      {/* Tag count indicator */}
      {tags.length > 0 && (
        <div className="text-xs text-muted-foreground mt-1">
          {tags.length} / {maxTags} tags
        </div>
      )}
    </div>
  );
}
